import React from 'react';
import {HashRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {Toaster} from 'react-hot-toast';
import {AuthProvider} from './contexts/AuthContext';
import {OrderProvider} from './contexts/OrderContext';
import {MembershipProvider} from './contexts/MembershipContext';
import {OperatorProvider} from './contexts/OperatorContext';

import AuthGuard from './components/guards/AuthGuard';
import RoleGuard from './components/guards/RoleGuard';
import MobileOnlyGuard from './components/guards/MobileOnlyGuard';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import OTPVerificationPage from './pages/auth/OTPVerificationPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';

// Customer Pages
import CustomerDashboard from './pages/customer/CustomerDashboard';
import ServiceTypeSelection from './pages/customer/ServiceTypeSelection';
import ServiceCategories from './pages/customer/ServiceCategories';
import ServiceDetails from './pages/customer/ServiceDetails';
import ServiceSelection from './pages/customer/ServiceSelection';
import DocumentUpload from './pages/customer/DocumentUpload';
import PaymentPage from './pages/customer/PaymentPage';
import PaymentReceipt from './pages/customer/PaymentReceipt';
import OrderTracking from './pages/customer/OrderTracking';
import MembershipPlans from './pages/customer/MembershipPlans';

// Operator Pages
import OperatorDashboard from './pages/operator/OperatorDashboard';
import AssignedOrders from './pages/operator/AssignedOrders';
import AvailableOrders from './pages/operator/AvailableOrders';
import OrderDetails from './pages/operator/OrderDetails';
import KycVerification from './pages/operator/KycVerification';
import OperatorWallet from './pages/operator/OperatorWallet';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ServiceManagement from './pages/admin/ServiceManagement';
import VerifyApplications from './pages/admin/VerifyApplications';
import ManageOperators from './pages/admin/ManageOperators';
import RevenueReports from './pages/admin/RevenueReports';
import AdminMembershipPlans from './pages/admin/MembershipPlans';
import PromotionManagement from './pages/admin/PromotionManagement';
import ContentManagement from './pages/admin/ContentManagement';
import AdminServices from './pages/admin/AdminServices';
import ServicesDatabase from './pages/admin/ServicesDatabase';
import AdminServiceEditor from './pages/admin/AdminServiceEditor';
import AdminServiceLinks from './pages/admin/AdminServiceLinks';
import DynamicServiceDetails from './pages/customer/DynamicServiceDetails';

// Shared Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ServiceInlineEditor from './components/admin/ServiceInlineEditor';

const ConditionalFooter = () => {
  return <Footer className="hidden sm:block" />;
};

function App() {
  return (
    <AuthProvider>
      <MembershipProvider>
        <OrderProvider>
          <OperatorProvider>
            <Router>
                <div className="min-h-screen bg-gray-50 flex flex-col">
                  <Navbar />
                  <main className="flex-1">
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/admin-login" element={<AdminLoginPage />} />

                      <Route path="/verify-otp" element={<OTPVerificationPage />} />

                      {/* Customer Routes */}
                      <Route path="/customer" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><CustomerDashboard /></RoleGuard></AuthGuard>} />
                      <Route path="/dashboard" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><CustomerDashboard /></RoleGuard></AuthGuard>} />
                      <Route path="/service-type-selection" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><ServiceTypeSelection /></RoleGuard></AuthGuard>} />
                      <Route path="/service-categories" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><ServiceCategories /></RoleGuard></AuthGuard>} />
                      <Route path="/service-details/:serviceId" element={<AuthGuard><RoleGuard allowedRoles={['customer', 'admin']}><DynamicServiceDetails /></RoleGuard></AuthGuard>} />
                      <Route path="/services" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><ServiceSelection /></RoleGuard></AuthGuard>} />
                      <Route path="/upload-documents" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><DocumentUpload /></RoleGuard></AuthGuard>} />
                      <Route path="/payment" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><PaymentPage /></RoleGuard></AuthGuard>} />
                      <Route path="/payment-receipt/:orderId?" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><PaymentReceipt /></RoleGuard></AuthGuard>} />
                      <Route path="/track-order/:orderId" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><OrderTracking /></RoleGuard></AuthGuard>} />
                      <Route path="/membership" element={<AuthGuard><RoleGuard allowedRoles={['customer']}><MembershipPlans /></RoleGuard></AuthGuard>} />
                      

                      {/* Operator Routes */}
                      <Route path="/operator" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><OperatorDashboard /></RoleGuard></AuthGuard>} />
                      <Route path="/operator/orders" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><AssignedOrders /></RoleGuard></AuthGuard>} />
                      <Route path="/operator/available" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><AvailableOrders /></RoleGuard></AuthGuard>} />
                      <Route path="/operator/order/:orderId" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><OrderDetails /></RoleGuard></AuthGuard>} />
                      <Route path="/operator/kyc" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><KycVerification /></RoleGuard></AuthGuard>} />
                      <Route path="/operator/wallet" element={<AuthGuard><RoleGuard allowedRoles={['operator']}><OperatorWallet /></RoleGuard></AuthGuard>} />

                      {/* Admin Routes */}
                      <Route path="/admin" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><AdminDashboard /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/service-management" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><ServiceManagement /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/verify-applications" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><VerifyApplications /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/operators" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><ManageOperators /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/revenue-reports" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><RevenueReports /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/membership-plans" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><AdminMembershipPlans /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/promotions" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><PromotionManagement /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/site-content" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><ContentManagement /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/services" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><AdminServices /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/services-database" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><ServicesDatabase /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/service-editor" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><AdminServiceEditor /></RoleGuard></AuthGuard>} />
                      <Route path="/admin/service-links" element={<AuthGuard><RoleGuard allowedRoles={['admin']}><AdminServiceLinks /></RoleGuard></AuthGuard>} />

                      {/* Catch all route */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    {/* Admin inline editor overlays when on service details */}
                    <ServiceInlineEditor />
                  </main>
                  <ConditionalFooter />
                  <Toaster position="top-right" />
                </div>
              </Router>
          </OperatorProvider>
        </OrderProvider>
      </MembershipProvider>
    </AuthProvider>
  );
}

export default App;